import { useEffect, useMemo, useState } from 'react'

import GShell from './gg/GShell.jsx'
import { PbccSectionTitle } from '../components/PbccLayout.jsx'
import AIAssistant from '../components/AIAssistant.jsx'
import CLUB from '../config/club.js'
import {
  SPECIAL_ORDER_STAGES as STAGES,
  SPECIAL_ORDER_CATEGORIES as CATEGORIES,
  watchSpecialOrders,
  createSpecialOrder, updateSpecialOrder, setSpecialOrderStatus, deleteSpecialOrder,
} from '../lib/specialOrders.js'

const PRIMARY = CLUB.primaryColor || 'var(--ge-primary)'
const ACCENT = CLUB.accentColor || 'var(--ge-primary)'
const today = () => new Date().toISOString().slice(0, 10)
const money = (n) => (n == null || n === '' ? null : `$${(Number(n) || 0).toFixed(2)}`)
const fmtDate = (iso) => {
  const d = iso ? new Date(`${iso}T00:00`) : null
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : (iso || '—')
}

// Badge tint per stage (green/gold family; final stage reads "done").
const STAGE_STYLE = {
  'Placed':          { bg: 'rgba(107,114,128,0.12)', fg: '#4B5563' },
  'Ordered':         { bg: 'rgba(37,99,235,0.12)',   fg: '#1D4ED8' },
  'Shipped':         { bg: 'rgba(99,102,241,0.14)',  fg: '#4338CA' },
  'Arrived':         { bg: 'rgba(34,168,90,0.14)',   fg: '#15803D' },
  'Member Notified': { bg: 'rgba(var(--ge-primary-rgb),0.20)',  fg: 'var(--ge-primary)' },
  'Picked Up':       { bg: 'rgba(var(--ge-primary-rgb),0.14)',    fg: 'var(--ge-primary)' },
}

export default function SpecialOrders() {
  const [raw, setRaw] = useState({})
  const [filter, setFilter] = useState('All')
  const [notifyFor, setNotifyFor] = useState(null)
  const [editFor, setEditFor] = useState(null)

  useEffect(() => { const off = watchSpecialOrders(setRaw); return () => off() }, [])

  const orders = useMemo(() =>
    Object.entries(raw || {})
      .map(([id, v]) => ({ id, ...v, stageHistory: v?.stageHistory || {} }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
  [raw])

  const counts = useMemo(() => {
    const c = { All: orders.length }
    STAGES.forEach((s) => { c[s] = 0 })
    orders.forEach((o) => { if (c[o.status] != null) c[o.status] += 1 })
    return c
  }, [orders])

  const visible = filter === 'All' ? orders : orders.filter((o) => o.status === filter)

  return (
    <GShell>
      <section>
        <PbccSectionTitle title="Special Orders" subtitle="Track member orders from placed to picked up." />
        <div className="mt-3"><NewOrderForm /></div>
      </section>

      {/* stage counts + filter */}
      <section>
        <div className="flex flex-wrap gap-2">
          <FilterChip label={`All ${counts.All}`} active={filter === 'All'} onClick={() => setFilter('All')} />
          {STAGES.map((s) => (
            <FilterChip key={s} label={`${s} ${counts[s]}`} stage={s} active={filter === s} onClick={() => setFilter(s)} />
          ))}
        </div>
      </section>

      {/* list */}
      <section className="pb-4">
        {visible.length === 0 ? (
          <p className="text-sm text-gray-500">{orders.length === 0 ? 'No special orders yet — add one above.' : `No orders at "${filter}".`}</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {visible.map((o) => (
              <OrderCard key={o.id} o={o} onNotify={() => setNotifyFor(o)} onEdit={() => setEditFor(o)} />
            ))}
          </div>
        )}
      </section>

      {notifyFor ? <NotifyModal order={notifyFor} onClose={() => setNotifyFor(null)} /> : null}
      {editFor ? <EditModal order={editFor} onClose={() => setEditFor(null)} /> : null}
      <AIAssistant tournamentId={null} />
    </GShell>
  )
}

// ── Order card ───────────────────────────────────────────────────────────
function OrderCard({ o, onNotify, onEdit }) {
  const [err, setErr] = useState('')
  const idx = STAGES.indexOf(o.status)
  const style = STAGE_STYLE[o.status] || STAGE_STYLE.Placed
  const move = async (dir) => {
    const next = STAGES[idx + dir]
    if (!next) return
    try { await setSpecialOrderStatus(o.id, next) } catch (e) { setErr(e?.message || 'Could not update.') }
  }
  const del = async () => {
    if (!window.confirm(`Delete the special order for ${o.name} (${o.item})?`)) return
    try { await deleteSpecialOrder(o.id) } catch (e) { setErr(e?.message || 'Delete failed.') }
  }
  return (
    <div className="rounded-2xl p-4" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)' }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-base font-extrabold" style={{ color: PRIMARY }}>{o.name}</div>
          <div className="text-sm text-gray-800 mt-0.5">{o.item}</div>
        </div>
        <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0" style={{ background: style.bg, color: style.fg }}>{o.status}</span>
      </div>
      <div className="text-xs text-gray-500 mt-2 flex flex-wrap gap-x-3 gap-y-0.5">
        {o.vendor ? <span>🏷 {o.vendor}</span> : null}
        <span>📦 {o.category}</span>
        <span>🗓 Placed {fmtDate(o.datePlaced)}</span>
        {money(o.cost) ? <span>💵 {money(o.cost)}</span> : null}
      </div>
      {o.notes ? <p className="text-xs text-gray-600 mt-1.5 leading-snug">{o.notes}</p> : null}

      {/* stage pipeline */}
      <div className="mt-3 flex items-center gap-2">
        <button type="button" className="btn-secondary text-xs" disabled={idx <= 0} onClick={() => move(-1)} title="Back a stage">◀</button>
        <div className="flex-1 text-center text-xs font-bold" style={{ color: PRIMARY }}>
          {o.status}
          {o.stageHistory?.[o.status] ? <span className="text-gray-400 font-normal"> · {fmtDate(o.stageHistory[o.status])}</span> : null}
        </div>
        <button type="button" className="btn-secondary text-xs" disabled={idx >= STAGES.length - 1} onClick={() => move(1)} title="Forward a stage">▶</button>
      </div>

      {/* actions */}
      <div className="mt-3 flex items-center gap-2 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
        <button type="button" className="btn-primary text-xs" onClick={onNotify}>🔔 Notify Member</button>
        <button type="button" className="btn-ghost text-xs" onClick={onEdit} style={{ color: ACCENT }}>Edit</button>
        <button type="button" className="btn-ghost text-xs ml-auto" style={{ color: '#B91C1C' }} onClick={del}>Delete</button>
      </div>
      {err ? <Err msg={err} /> : null}
    </div>
  )
}

// ── Notify (marks Member Notified + copyable message) ────────────────────
function NotifyModal({ order, onClose }) {
  const [copied, setCopied] = useState(false)
  const [marked, setMarked] = useState(order.status === 'Member Notified' || order.status === 'Picked Up')
  const [err, setErr] = useState('')
  const first = (order.name || '').trim().split(/\s+/)[0] || order.name
  const message = `Hi ${first}, your ${order.item} has arrived at the ${CLUB.name} pro shop and is ready for pickup.`

  const markNotified = async () => {
    try { await setSpecialOrderStatus(order.id, 'Member Notified'); setMarked(true) }
    catch (e) { setErr(e?.message || 'Could not update status.') }
  }
  const copy = async () => {
    try { await navigator.clipboard.writeText(message); setCopied(true); setTimeout(() => setCopied(false), 2000) }
    catch { setErr('Copy failed — select the text and copy manually.') }
  }

  return (
    <Modal title={`Notify ${order.name}`} onClose={onClose}>
      <p className="text-sm text-gray-600 mb-2">Ready-to-send message:</p>
      <div className="rounded-xl p-3 text-sm" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, color: PRIMARY }}>{message}</div>
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <button type="button" className="btn-primary text-sm" onClick={copy}>{copied ? 'Copied ✓' : '📋 Copy message'}</button>
        {marked ? (
          <span className="text-xs font-bold" style={{ color: '#15803D' }}>✓ Marked "Member Notified"</span>
        ) : (
          <button type="button" className="btn-secondary text-sm" onClick={markNotified}>Mark "Member Notified"</button>
        )}
      </div>
      <p className="text-[11px] text-gray-400 mt-3 leading-snug">
        Automatic text/email is a future add-on — for now, copy this and send it from your phone or email.
      </p>
      {err ? <Err msg={err} /> : null}
    </Modal>
  )
}

// ── Create / edit forms ──────────────────────────────────────────────────
function NewOrderForm() {
  const [f, setF] = useState({ name: '', item: '', vendor: '', category: 'Clubs', datePlaced: today(), cost: '', notes: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const submit = async () => {
    setBusy(true); setErr('')
    try { await createSpecialOrder(f); setF({ name: '', item: '', vendor: '', category: 'Clubs', datePlaced: today(), cost: '', notes: '' }) }
    catch (e) { setErr(e?.message || 'Could not create order.') }
    finally { setBusy(false) }
  }
  return (
    <div className="rounded-2xl p-4" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}30`, boxShadow: '0 6px 16px rgba(0,0,0,0.06)' }}>
      <div className="text-xs font-extrabold uppercase tracking-widest mb-2" style={{ color: ACCENT }}>New Special Order</div>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="input" placeholder="Customer / member name" value={f.name} onChange={set('name')} />
        <input className="input" placeholder="Item description" value={f.item} onChange={set('item')} />
        <input className="input" placeholder="Vendor (Titleist, Callaway…)" value={f.vendor} onChange={set('vendor')} />
        <select className="input" value={f.category} onChange={set('category')}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="input" type="date" value={f.datePlaced} onChange={set('datePlaced')} />
        <input className="input" type="number" step="0.01" min="0" placeholder="Cost (optional)" value={f.cost} onChange={set('cost')} />
      </div>
      <input className="input mt-2 w-full" placeholder="Notes (optional)" value={f.notes} onChange={set('notes')} />
      <button type="button" className="btn-primary text-sm mt-2" onClick={submit} disabled={busy || !f.name.trim() || !f.item.trim()}>{busy ? 'Adding…' : 'Add Order'}</button>
      {err ? <Err msg={err} /> : null}
    </div>
  )
}

function EditModal({ order, onClose }) {
  const [f, setF] = useState({
    name: order.name || '', item: order.item || '', vendor: order.vendor || '',
    category: order.category || 'Clubs', datePlaced: order.datePlaced || today(),
    cost: order.cost == null ? '' : String(order.cost), notes: order.notes || '',
  })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }))
  const save = async () => {
    setBusy(true); setErr('')
    try { await updateSpecialOrder(order.id, f); onClose() }
    catch (e) { setErr(e?.message || 'Could not save.'); setBusy(false) }
  }
  return (
    <Modal title={`Edit — ${order.name}`} onClose={onClose}>
      <div className="grid sm:grid-cols-2 gap-2">
        <input className="input" placeholder="Customer / member name" value={f.name} onChange={set('name')} />
        <input className="input" placeholder="Item description" value={f.item} onChange={set('item')} />
        <input className="input" placeholder="Vendor" value={f.vendor} onChange={set('vendor')} />
        <select className="input" value={f.category} onChange={set('category')}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input className="input" type="date" value={f.datePlaced} onChange={set('datePlaced')} />
        <input className="input" type="number" step="0.01" min="0" placeholder="Cost (optional)" value={f.cost} onChange={set('cost')} />
      </div>
      <input className="input mt-2 w-full" placeholder="Notes (optional)" value={f.notes} onChange={set('notes')} />
      <div className="flex justify-end gap-2 mt-3">
        <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary text-sm" onClick={save} disabled={busy || !f.name.trim() || !f.item.trim()}>{busy ? 'Saving…' : 'Save'}</button>
      </div>
      {err ? <Err msg={err} /> : null}
    </Modal>
  )
}

// ── Small shared UI ──────────────────────────────────────────────────────
function FilterChip({ label, stage, active, onClick }) {
  const s = stage ? STAGE_STYLE[stage] : null
  return (
    <button type="button" onClick={onClick}
      className="text-xs font-bold px-3 py-1.5 rounded-full transition"
      style={active
        ? { background: PRIMARY, color: '#FFFFFF', border: `1px solid ${PRIMARY}` }
        : { background: s ? s.bg : 'rgba(0,0,0,0.05)', color: s ? s.fg : '#4B5563', border: '1px solid transparent' }}>
      {label}
    </button>
  )
}

function Err({ msg }) {
  return <p className="text-xs font-bold mt-1" style={{ color: '#B91C1C' }}>{msg}</p>
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(var(--ge-primary-rgb),0.78)' }} onClick={onClose}>
      <div className="w-full max-w-xl rounded-2xl my-8" style={{ background: CLUB.creamColor, border: `1px solid ${ACCENT}44`, boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${ACCENT}33` }}>
          <h3 className="text-lg font-extrabold" style={{ color: PRIMARY, fontFamily: 'Cinzel, serif' }}>{title}</h3>
          <button type="button" onClick={onClose} className="text-2xl leading-none" style={{ color: PRIMARY }} aria-label="Close">×</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}
